import { Injectable, Type } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DateRangeSelectDialogComponent } from './date-range-select-dialog/date-range-select-dialog.component';
import { ConfigurationItem, DateFilter, DateFilterOption, Filter, FilterEqual, LaboratorySample, NetworkConstants, filterObjects } from 'diwimamodelweb/lib';
import { skipWhile } from 'rxjs/operators';
import { SettingsService } from 'src/app/shared/settings.service';
import { TranslateService } from '@ngx-translate/core';
import { LabSampleService } from 'src/app/shared/lab-sample.service';
import { LabSampleMultiSelectFilterDialogComponent } from './lab-sample-multi-select-filter-dialog/lab-sample-multi-select-filter-dialog.component';

export interface FilterItemCallback {
  onFilterItemSelected: (item: FilterSelectItem) => void
  onFilterItemDeselected: (item: FilterSelectItem) => void
  onActiveFiltersChanged: (activeFilters: Filter[]) => void
}

/**
 * Function that returns the start and end Dates of the current hunting season 
 * in Germany this is 01.04.x -> 31.03.x + 1
 * 
 * @returns {start: Date, end: Date}
 */
export const getCurrentJagdJahr = (): {start: Date, end: Date} => {
  const startString = `-04-01T00:00:00`
  const endString = `-03-31T23:59:59`
  const now = new Date()
  const year = now.getFullYear()  // eg. 2021
  const month = now.getMonth()    // 0-11
  if (month < 3) {
    // meaning start in last year
    return {start: new Date(`${year - 1}${startString}`), end: new Date(`${year}${endString}`)}
  } else {
    return {start: new Date(`${year}${startString}`), end: new Date(`${year + 1}${endString}`)}
  }
}

/**
 * Function that returns the start and end Dates of the previous hunting season 
 * in Germany this is 01.04.x -> 31.03.x + 1
 * 
 * @returns {start: Date, end: Date}
 */
 export const getPreviousJagdJahr = (): {start: Date, end: Date} => {
  const startString = `-04-01T00:00:00`
  const endString = `-03-31T23:59:59`
  const now = new Date()
  const year = now.getFullYear()  // eg. 2021
  const month = now.getMonth()    // 0-11
  if (month < 3) {
    // meaning start in last year
    return {start: new Date(`${year - 2}${startString}`), end: new Date(`${year - 1}${endString}`)}
  } else {
    return {start: new Date(`${year - 1}${startString}`), end: new Date(`${year}${endString}`)}
  }

}

export class FilterSelectItem {
  
  public activeFilters: Map<string, Filter> = new Map()
  public listeners: any[] = []

  // public componentRef: Type<any> = MultiSelectDialogComponent GrMultiSelectFilterDialogComponent
  // public componentRef: Type<any> =  GrMultiSelectFilterDialogComponent
  public componentRef: Type<any> =  LabSampleMultiSelectFilterDialogComponent
  
  constructor(
    public settingId: string,
    public title: string,
    public filterCallback: ((filter: Filter[]) => void),
    public options?: [{selected: boolean, item: ConfigurationItem}],
    public sortOrder?: Number
  ) {
    options?.forEach(element => {
      if (element.selected && element.item) {
        this.buildFilter(settingId, element.item)
      }
    });
    // if (settingId == "timeStamp") this.componentRef = DateRangeSelectDialogComponent
    if (settingId == "updateTimestamp") this.componentRef = DateRangeSelectDialogComponent
    // if (settingId == "timeStamp") this.componentRef = SingleSelectDialogComponent
  }

  deselectFilter(identifier) {
    delete this.activeFilters[identifier]
  }
  setActiveFilter(selectedOptions: [{selected: boolean, item: ConfigurationItem}]) {
    this.activeFilters.clear()
    const identifierList = selectedOptions.map(el => el.item?.identifier)
    this.options.forEach((option) => {

      if (identifierList.includes(option.item.identifier)){
        if(!option.selected) {
          option.selected = true
        }
        this.buildFilter(this.settingId, option.item)
      } else {
        option.selected = false
        this.deselectFilter(option.item.identifier)
      }
    })
    this.onFilter()
  }

  buildFilter(settingId: string, option: any) {
    switch(settingId) {
      case(NetworkConstants.SETTING_LAB_SAMPLE_LOCATION): {
        this.activeFilters.set(option.identifier,
          new FilterEqual(
            `${settingId}_${option.identifier}`,
            'currentLocation',
            option.identifier
          )
        )
        break
      }
      case(NetworkConstants.SETTING_LAB_SAMPLE_STATUS): {
        this.activeFilters.set(option.identifier,
          new FilterEqual(
            `${settingId}_${option.identifier}`,
            'status',
            option.identifier
          )
        )
        break
      }
      case(NetworkConstants.SETTING_LAB_SAMPLE_TYPE): {
        this.activeFilters.set(option.identifier,
          new FilterEqual(
            `${settingId}_${option.identifier}`,
            'type',
            option.identifier
          )
        )
        break
      }
      case("updateTimestamp"): {
        const dateFilter = new DateFilter(
          `timeStamp_${option.identifier}`,
          'updateTimestamp',
          option.dateFilterOption,
          option.filterValue
        )
        this.activeFilters.set(option.identifier,
          dateFilter
        )
        break
      }
    }
  }

  onFilter() {
    this.onActiveFiltersChanged()
    this.filterCallback([...this.activeFilters.values()])
  }

  onFilterItemSelected() {
    this.listeners.forEach((listener) => {
      listener.onFilterItemSelected(this)
    })
  };
  onFilterItemDeselected() {
    this.options.forEach(el => el.selected = false)
    this.listeners.forEach((listener) => {
      listener.onFilterItemDeselected(this)
    })
  }
  onActiveFiltersChanged() {
    this.listeners.forEach((listener) => {
      listener.onActiveFiltersChanged([...this.activeFilters.values()])
    })
  }
}

@Injectable({
  providedIn: 'root'
})

export class FilterLabSampleService {
  availableFilter$ = new BehaviorSubject<Map<string, FilterSelectItem>>(new Map())
  selectedFilterItem$ = new BehaviorSubject<FilterSelectItem[]>([])
  filteredLabeSamples$ = new BehaviorSubject<LaboratorySample[]>(undefined)
  customDate:boolean = false
  filtered_customDate$: any
  
  constructor(
    private labSampleService: LabSampleService,
    private settingsService: SettingsService,
    private translateService: TranslateService,
  ) {

    this.settingsService.selectedUserContext$.subscribe((sel) => {
      if (!sel) {
        // reset values
        this.availableFilter$.next(new Map())
        this.selectedFilterItem$.next([])
      }else{
        this.selectedFilterItem$.next([])
        this.availableFilter$.next(new Map())
        this.loadTimeStampFilter()
        if(sel.identifier === this.settingsService.lastContextSelected$.value){
          this.loadSettingsData('filter_location', this.settingsService.settingsSampleLocation.value, 2)
          this.loadSettingsData('filter_status', this.settingsService.settingsSampleStatus.value, 2)
          this.loadSettingsData('filter_type', this.settingsService.settingsSampleType.value, 3)
        }
      }
    })

    this.settingsService.settingsSampleLocation.subscribe(async (setting) => {
      this.loadSettingsData('filter_location', setting, 2)
    });

    this.settingsService.settingsSampleStatus.subscribe(async (setting) => {
      this.loadSettingsData('filter_status', setting, 3)
    });

    this.settingsService.settingsSampleType.subscribe(async (setting) => {
      this.loadSettingsData('filter_type', setting, 3)
    });

    
    (async () => {
      this.loadTimeStampFilter()
    }) ();

    this.labSampleService.labSampleSubject$.pipe(skipWhile(el => !el)).subscribe(
      (labSamples) => {
        this.filteredLabeSamples$.next(labSamples)
      },
      (error) => {
        console.error(error)
      }
    )
  }

  addFilterItem(filter: FilterSelectItem) {
    let allFilters = this.selectedFilterItem$.value
    const ind = allFilters?.findIndex(el => el.settingId == filter.settingId)
    if (ind != -1) return
    if (!allFilters) allFilters = []
    allFilters.push(filter)
    this.selectedFilterItem$.next(allFilters)
    filter.onFilterItemSelected()
  }

  removeFilterItem(filter: FilterSelectItem) {
    let allFilters = this.selectedFilterItem$.value
    const ind = allFilters.findIndex(el => el.settingId == filter.settingId)
    if (ind != -1) allFilters.splice(ind, 1)
    else return 
    this.selectedFilterItem$.next(allFilters)
    if (allFilters.length > 0) {
      let filters = []
      allFilters.forEach((item) => {
        filters = filters.concat([...item.activeFilters.values()])
      })
      this.filter()
    } else {
      this.filter()
    }
    filter.onFilterItemDeselected()
  }

  private setAvailabeFilter(title: string, key: string, options: any[], sortOrder: Number) {
    const availabeFilter = this.availableFilter$.value
    let opts: any = []
    options.forEach((item) => {
      opts.push({selected: false, item})
    })
    availabeFilter.set(key, 
      new FilterSelectItem(
        key,
        title,
        (filter: Filter[]) => {
          this.filter()
        },
        opts,
        sortOrder
      )
    )
    this.availableFilter$.next(availabeFilter)
  }

  filter() {  
    if (this.selectedFilterItem$.value.length > 0) {
      const fl = this.selectedFilterItem$.value.map(el => [...el.activeFilters.values()])

      fl.forEach( filters => {
        filters.forEach(filter => {
          if(filter.filterName == "timeStamp_custom"){
            this.customDate=true
            this.filtered_customDate$ = filter.filterValue
          }else{
            this.customDate=false
            this.filtered_customDate$ = {}
            this.setFilteredCustomDate()
          }
        })
      })
      const data = filterObjects(this.labSampleService.labSampleSubject$.value, fl)
      this.filteredLabeSamples$.next(data)
    } else {
      this.filteredLabeSamples$.next(this.labSampleService.labSampleSubject$.value)
    }
  }


  setFilteredCustomDate(value?:any){
    if(value){
      this.customDate=true
      this.filtered_customDate$ = value
    }else{
      this.customDate=false
      this.filtered_customDate$ = {}
    }
  }
  
  async loadTimeStampFilter(){
    const dateRangeItemTitles = await this.translateService.instant('filter_date_range_items')
    const currentHuntingYear = getCurrentJagdJahr()
    const previousHuntingYear = getPreviousJagdJahr()
    const options = [
      {
        identifier: "today",
        title: dateRangeItemTitles["today"],
        dateFilterOption: DateFilterOption.AFTER,
        filterValue: {baseValue: new Date(new Date().setHours(0,0,0,0) )}
        // filterValue: {baseValue: new Date(new Date()), firstHour: new Date(new Date().setHours(0,0,0,0) )}
      },
      {
        identifier: "current_year",
        title: dateRangeItemTitles["current_year"],
        dateFilterOption: DateFilterOption.AFTER,
        filterValue: {baseValue: currentHuntingYear.start}
      },
      {
        identifier: "last_year",
        title: dateRangeItemTitles["last_year"],
        dateFilterOption: DateFilterOption.BETWEEN,
        filterValue: {startDate: previousHuntingYear.start, endDate: previousHuntingYear.end}
      },
      {
        identifier: "custom",
        title: dateRangeItemTitles["custom"],
        dateFilterOption: DateFilterOption.BETWEEN,
        filterValue: {startDate: previousHuntingYear.start, endDate: previousHuntingYear.end}
      }
    ]
    const filterDateRangeTitle = await this.translateService.instant('filter_date_range')
    this.setAvailabeFilter(filterDateRangeTitle, "updateTimestamp", options, 7)
  }

  async loadSettingsData(translate, setting, sortOrder){
    if (!setting) return
    const title = await this.translateService.instant(translate)
    this.setAvailabeFilter(title, setting.identifier, setting.values, sortOrder)
  }
}